import React, { useEffect, useState, useCallback } from "react";
import { Save, Palette, Image as ImageIcon, MessageSquare, Layout as LayoutIcon, Info, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useBranding } from "@/context/BrandingContext";

/**
 * Admin Branding Settings — White Label Foundation v1.
 *
 * 5 sections: General, Visual, Theme, Contact, Footer.
 * PUT /admin/settings/branding untuk save.
 * BrandingContext.reload() dipanggil setelah save supaya CSS vars + title update instant.
 */
export default function AdminBrandingSettings() {
  const { reload } = useBranding();
  const [b, setB] = useState({
    general: { name: "", short_name: "", tagline: "", description: "" },
    visual: { logo: "", logo_dark: "", favicon: "", og_image: "", default_banner: "" },
    theme: { primary: "", secondary: "", accent: "", background: "", text: "" },
    contact: { whatsapp: "", email: "", instagram: "", facebook: "", tiktok: "", youtube: "", address: "", maps_url: "" },
    footer: { text: "", copyright: "", powered_by: "" },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/branding");
      if (r.data) setB(r.data);
    } catch (e) { notifyError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (section, field, value) =>
    setB((x) => ({ ...x, [section]: { ...(x[section] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/branding", b);
      toast.success("Branding tersimpan. Halaman akan otomatis update.");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  // Upload helper — pakai endpoint /admin/uploads existing (returns {path})
  const uploadAndSet = async (file, section, field) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data?.url) upd(section, field, r.data.url);
      else if (r.data?.path) upd(section, field, r.data.path);
      toast.success(`Upload ${field} berhasil`);
    } catch (e) { notifyError(e); }
  };

  if (loading) {
    return <AdminLayout><div className="p-8 text-center text-[#5C5C5C]">Memuat branding...</div></AdminLayout>;
  }

  return (
    <AdminLayout title="Platform Branding">
      <div className="max-w-4xl space-y-6" data-testid="admin-branding-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-black">Platform Branding</h1>
            <p className="text-sm text-[#5C5C5C] mt-1">Ubah identitas website. Semua perubahan berlaku instant tanpa deploy ulang.</p>
          </div>
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl font-bold h-11 px-5" data-testid="branding-save-btn">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Branding"}
          </Button>
        </div>

        {/* General */}
        <Card className="p-6" data-testid="branding-general-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Info size={18} className="brand-text-primary" /> General</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Website Name</Label><Input value={b.general.name || ""} onChange={(e) => upd("general", "name", e.target.value)} placeholder="Toko Anda" data-testid="branding-name" /></div>
            <div><Label>Short Name (PWA/mobile)</Label><Input value={b.general.short_name || ""} onChange={(e) => upd("general", "short_name", e.target.value)} placeholder="TokoAnda" /></div>
            <div className="sm:col-span-2"><Label>Tagline</Label><Input value={b.general.tagline || ""} onChange={(e) => upd("general", "tagline", e.target.value)} placeholder="Segar & Terpercaya" /></div>
            <div className="sm:col-span-2"><Label>Short Description</Label><Textarea value={b.general.description || ""} onChange={(e) => upd("general", "description", e.target.value)} placeholder="Telur premium ..." rows={2} /></div>
          </div>
        </Card>

        {/* Visual */}
        <Card className="p-6" data-testid="branding-visual-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><ImageIcon size={18} className="brand-text-primary" /> Visual Assets</h3>
          <div className="space-y-3">
            {[
              { field: "logo", label: "Logo (Utama)" },
              { field: "logo_dark", label: "Logo Dark Mode (opsional)" },
              { field: "favicon", label: "Favicon" },
              { field: "og_image", label: "OG Image (untuk share sosmed)" },
              { field: "default_banner", label: "Default Banner" },
            ].map((it) => (
              <div key={it.field} className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label>{it.label}</Label>
                  <Input
                    value={b.visual[it.field] || ""}
                    onChange={(e) => upd("visual", it.field, e.target.value)}
                    placeholder="/brand/... atau https://..."
                    className="font-mono text-xs"
                    data-testid={`branding-visual-${it.field}`}
                  />
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAndSet(e.target.files?.[0], "visual", it.field)} />
                  <span className="inline-flex items-center gap-1 px-3 h-9 rounded-xl border border-[#E5E5E5] text-xs hover:bg-[#FAF9F6]"><Upload size={12} /> Upload</span>
                </label>
              </div>
            ))}
          </div>
        </Card>

        {/* Theme */}
        <Card className="p-6" data-testid="branding-theme-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Palette size={18} className="brand-text-primary" /> Theme Colors</h3>
          <p className="text-xs text-[#5C5C5C] mb-3">Warna diinject sebagai CSS variables (--brand-primary, dll). Berlaku instant ke semua halaman.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {["primary", "secondary", "accent", "background", "text"].map((k) => (
              <div key={k}>
                <Label className="capitalize">{k}</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={b.theme[k] || "#000000"}
                    onChange={(e) => upd("theme", k, e.target.value)}
                    className="h-10 w-14 rounded border border-[#E5E5E5]"
                    data-testid={`branding-theme-${k}-picker`}
                  />
                  <Input
                    value={b.theme[k] || ""}
                    onChange={(e) => upd("theme", k, e.target.value)}
                    placeholder="#HEX"
                    className="font-mono text-xs"
                    data-testid={`branding-theme-${k}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {["primary", "secondary", "accent", "background", "text"].map((k) => (
              <div key={`prev-${k}`} className="flex items-center gap-1 text-[10px]">
                <span className="inline-block w-6 h-6 rounded border border-[#E5E5E5]" style={{ backgroundColor: b.theme[k] || "#fff" }} />
                <span className="capitalize font-bold">{k}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Contact */}
        <Card className="p-6" data-testid="branding-contact-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><MessageSquare size={18} className="brand-text-primary" /> Kontak & Sosial Media</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>WhatsApp (62xxx)</Label><Input value={b.contact.whatsapp || ""} onChange={(e) => upd("contact", "whatsapp", e.target.value)} placeholder="6281234567890" data-testid="branding-whatsapp" /></div>
            <div><Label>Email</Label><Input value={b.contact.email || ""} onChange={(e) => upd("contact", "email", e.target.value)} placeholder="halo@tokoanda.com" data-testid="branding-email" /></div>
            <div><Label>Instagram</Label><Input value={b.contact.instagram || ""} onChange={(e) => upd("contact", "instagram", e.target.value)} placeholder="@tokoanda" /></div>
            <div><Label>Facebook</Label><Input value={b.contact.facebook || ""} onChange={(e) => upd("contact", "facebook", e.target.value)} placeholder="tokoanda.official" /></div>
            <div><Label>TikTok</Label><Input value={b.contact.tiktok || ""} onChange={(e) => upd("contact", "tiktok", e.target.value)} placeholder="@tokoanda" /></div>
            <div><Label>YouTube</Label><Input value={b.contact.youtube || ""} onChange={(e) => upd("contact", "youtube", e.target.value)} placeholder="@tokoanda" /></div>
            <div className="sm:col-span-2"><Label>Alamat</Label><Textarea value={b.contact.address || ""} onChange={(e) => upd("contact", "address", e.target.value)} rows={2} /></div>
            <div className="sm:col-span-2"><Label>Google Maps URL</Label><Input value={b.contact.maps_url || ""} onChange={(e) => upd("contact", "maps_url", e.target.value)} placeholder="https://maps.google.com/..." /></div>
          </div>
        </Card>

        {/* Footer */}
        <Card className="p-6" data-testid="branding-footer-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><LayoutIcon size={18} className="brand-text-primary" /> Footer</h3>
          <div className="grid gap-3">
            <div><Label>Footer Text</Label><Textarea value={b.footer.text || ""} onChange={(e) => upd("footer", "text", e.target.value)} placeholder="Deskripsi singkat brand..." rows={2} /></div>
            <div><Label>Copyright Owner</Label><Input value={b.footer.copyright || ""} onChange={(e) => upd("footer", "copyright", e.target.value)} placeholder="Nama Toko Anda" /></div>
            <div><Label>Powered By (optional)</Label><Input value={b.footer.powered_by || ""} onChange={(e) => upd("footer", "powered_by", e.target.value)} placeholder="e.g. Emergent Platform" /></div>
          </div>
        </Card>

        <div className="sticky bottom-4 flex gap-2">
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl h-12 px-6 font-bold shadow-lg" data-testid="branding-save-btn-bottom">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Branding"}
          </Button>
          <Button onClick={load} variant="outline" className="rounded-xl h-12" data-testid="branding-reload-btn">
            <RefreshCw size={16} className="mr-1" /> Reload
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
